import { alpha, type SxProps, type Theme } from "@mui/material/styles";

import type {
  DashboardStatCardAccent,
  DashboardStatTrendTone,
} from "./DashboardStatCard.types";

const accentColor = (theme: Theme, accent: DashboardStatCardAccent) => {
  if (accent === "warning") return theme.palette.warning.main;
  if (accent === "secondary") return theme.palette.secondary.main;
  return theme.palette.primary.main;
};

export const root =
  (accent: DashboardStatCardAccent): SxProps<Theme> =>
  (theme) => ({
    position: "relative",
    overflow: "hidden",
    borderRadius: 2.5,
    p: 2.75,
    bgcolor: "background.paper",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[2],
    display: "flex",
    flexDirection: "column",
    gap: 2,
    "&::before": {
      content: '""',
      position: "absolute",
      insetInlineStart: 0,
      top: 20,
      bottom: 20,
      width: 4,
      borderRadius: "0 4px 4px 0",
      bgcolor: accentColor(theme, accent),
    },
  });

export const topRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

export const iconWrap =
  (accent: DashboardStatCardAccent): SxProps<Theme> =>
  (theme) => ({
    width: 46,
    height: 46,
    borderRadius: 1.75,
    bgcolor: alpha(accentColor(theme, accent), 0.1),
    color: accentColor(theme, accent),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  });

const trendPalette = (
  theme: Theme,
  tone: DashboardStatTrendTone,
): { color: string; bgcolor: string } => {
  if (tone === "success") {
    return { color: theme.palette.success.main, bgcolor: alpha(theme.palette.success.main, 0.1) };
  }
  if (tone === "warning") {
    return { color: theme.palette.warning.main, bgcolor: alpha(theme.palette.warning.main, 0.12) };
  }
  if (tone === "secondary") {
    return {
      color: theme.palette.secondary.main,
      bgcolor: alpha(theme.palette.secondary.main, 0.12),
    };
  }
  return { color: theme.palette.text.disabled, bgcolor: theme.palette.background.default };
};

export const trendBadge =
  (tone: DashboardStatTrendTone): SxProps<Theme> =>
  (theme) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    fontSize: "0.75rem",
    fontWeight: 700,
    px: 1.25,
    py: 0.625,
    borderRadius: 15,
    ...trendPalette(theme, tone),
  });

export const valueBlock: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
};

export const value: SxProps<Theme> = {
  fontWeight: 800,
  fontSize: { xs: "1.625rem", sm: "1.875rem" },
  lineHeight: 1.1,
  color: "text.primary",
};

export const valueMuted: SxProps<Theme> = {
  fontWeight: 800,
  fontSize: { xs: "1.625rem", sm: "1.875rem" },
  lineHeight: 1.1,
  color: "text.disabled",
};

export const label: SxProps<Theme> = {
  fontSize: "0.84375rem",
  color: "text.secondary",
};
